import { autoinject, computedFrom, transient } from "aurelia-framework";
import { NonConformitySectionCountsModel, RightShipInspectionClientModel, RightShipInspectionNonConformityModel, RSInspetionNonConformityRFIModel } from "../../../models/rightship-inspection.models";
import { RightShipInspectionsService } from "../../../services/rightship-inspection.service";
import { AttachmentsHelper } from '@rightship-tech/common-ui/helpers/attachments.helper';
import { FileHelper } from "@rightship-tech/common-ui/helpers/file-helper";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { AttachmentsClientService } from '@rightship-tech/common-ui/services/attachments/attachments-client.service';
import * as Enumerable from "linq";
import { BindingSignaler } from "aurelia-templating-resources";
import { FindingRiskClassificationEnum, FindingRiskLevelEnum } from "../../../models/rightship-inspection.models";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";
import JSZip from 'jszip';
import { CommonUiDialogConstants } from "@rightship-tech/common-ui/constants/dialog.constants";
import { AttachmentDrawerSection, AttachmentType, AttachmentViewerExecutionModel } from "@rightship-tech/common-ui/models/attachments.models";

@autoinject
@transient()
export class PartialRightshipInspectionCustomerFindings
{
    sectionCounts: NonConformitySectionCountsModel;
    nonConformityRFI: RSInspetionNonConformityRFIModel;
    nonConformities: RightShipInspectionNonConformityModel[];
    nonConformitiesPerSection: RightShipInspectionNonConformityModel[];
    filteredNonConformities: RightShipInspectionNonConformityModel[];
    nonConformitiesSections: { sectionId: number, sectionNumber: string, sectionName: string }[];
    nonConformityCloseoutSubtaskType: number;
    findingsSectionInView: RightShipInspectionNonConformityModel = null;
    allSectionsSelected: boolean = false;
    totalFindingsCount: number;
    isLoading: boolean;
    riskLevelHighActive: boolean;
    riskLevelMediumActive: boolean;
    riskLevelLowActive: boolean;
    regulatoryFilterActive: boolean;
    industryStandardFilterActive: boolean;
    selectedFindingId: number;
    public totalAttachmentCount: number = 0;
    public allAttachmentSelected: boolean = false;
    public loadingCloseOutDocuments: boolean = false;
    public questionNumber: string;
    public item: RightShipInspectionClientModel;
    public toggleInspectionDetailDrawer: (model: AttachmentViewerExecutionModel) => void;
    public refreshHistoryTrail: () => void;
    public blob: string = ConfigService.instance().config.urls.blobStorageUrl;

    private _thumbnails: { name: string, url: string }[] = [];

    riskLevels: { [id: number]: string} = 
        { 
            [FindingRiskLevelEnum.High]: "High",
            [FindingRiskLevelEnum.Medium]:"Medium",
            [FindingRiskLevelEnum.Low]: "Low" 
        };

    riskClassifications: { [id: number]: string} = 
        { 
            [FindingRiskClassificationEnum.Regulatory]: "Regulatory",
            [FindingRiskClassificationEnum.IndustryStandard]: "Industry standard" 
        };

    riskLevelsCheckboxes: { id: number, name: string}[] = [
            { id: FindingRiskLevelEnum.High, name: "High" },
            { id: FindingRiskLevelEnum.Medium, name: "Medium" },
            { id: FindingRiskLevelEnum.Low, name: "Low" }
        ];

    riskClassificationsCheckboxes: { id: number, name: string}[] = [
        { id: FindingRiskClassificationEnum.Regulatory, name: "Regulatory" },
        { id: FindingRiskClassificationEnum.IndustryStandard, name: "Industry standard" }
    ];

    constructor(private rightshipInspectionService: RightShipInspectionsService,
        private dialogService: DialogClientService, private attachmentService: AttachmentsClientService, private bindingSignaler: BindingSignaler) {
    }

    @computedFrom("sectionCounts")
    get sections(): string[] {
        let sections = this.sectionCounts? Object.keys(this.sectionCounts): [];    
        //Object.keys orders by string which means the ones with the letters will come last
        //Here we order by the number and forget about the letter altogether    
        sections = Enumerable.from(sections).orderBy(x => (Number)(x.replace(/\D+/g, ''))).toArray();
        return sections;
    }

    @computedFrom("nonConformitiesPerSection")
    get riskLevelHighCount(){
        return this.nonConformitiesPerSection.filter((x) => (x.riskLevelId ? x.riskLevelId : x.defaultRiskLevelId) == FindingRiskLevelEnum.High).length;
    }

    @computedFrom("nonConformitiesPerSection")
    get riskLevelMediumCount(){
        return this.nonConformitiesPerSection.filter((x) => (x.riskLevelId ? x.riskLevelId : x.defaultRiskLevelId) == FindingRiskLevelEnum.Medium).length;
    }

    @computedFrom("nonConformitiesPerSection")
    get riskLevelLowCount(){
        return this.nonConformitiesPerSection.filter((x) => (x.riskLevelId ? x.riskLevelId : x.defaultRiskLevelId) == FindingRiskLevelEnum.Low).length;
    }

    @computedFrom("nonConformitiesPerSection")
    get regulatoryFilterCount(){
        return this.nonConformitiesPerSection.filter((x) => x.riskClassificationId === 1).length;
    }

    @computedFrom("nonConformitiesPerSection")
    get industryStandardFilterCount(){
        return this.nonConformitiesPerSection.filter((x) => x.riskClassificationId !== 1).length;
    }

    selectFindingSection(section: RightShipInspectionNonConformityModel){
        this.allSectionsSelected = false;
        this.allAttachmentSelected = false;
        this.findingsSectionInView = section;
        this.nonConformitiesPerSection = this.nonConformities.filter((x) => x.sectionNumber == section.sectionNumber);

        Enumerable.from(this.nonConformitiesPerSection).where(x => x.attachments && x.attachments.length > 0).selectMany(x => x.attachments)
            .where(x => !x["fullUrl"])
            .forEach(async attachment => {
                await this.addAttachmentToken(attachment);
            });

        this.resetCollapsableToggles();

        return this.setFilteredNonConformities();
    }

    selectAllSections(){
        this.allSectionsSelected = true;
        this.allAttachmentSelected = false;
        this.findingsSectionInView = null;
        this.nonConformitiesPerSection = this.nonConformities;

        this.resetCollapsableToggles();

        const filteredNonConformities = this.setFilteredNonConformities();

        return filteredNonConformities;
    }


    async selectAllAttachments(thumbnailId: string) {
        try{
            this.loadingCloseOutDocuments = true;
            const rsDocumentLinksResponse = await this.rightshipInspectionService.getRightShipInspectionDocumentLinks(this.item.rightShipInspectionId);
            this.loadingCloseOutDocuments = false;
            const getAttachmentDocuments = async () => {
                const documentLinks = rsDocumentLinksResponse.documentLinks;

                await Promise.all(documentLinks.map(async (document) => {
                    if (document.aiRunId) {
                        const zip = new JSZip();
                        const thumbnailResponse = await this.rightshipInspectionService.getInspectionThumbnailZipFile(document.aiRunId);
                        await zip.loadAsync(thumbnailResponse);

                        const thumbnailPromises = [];
                        zip.forEach((relativePath, zipEntry) => {
                            if (!zipEntry.dir && /\.(jpg|jpeg|png|gif)$/.test(relativePath)) { // Filter for image types
                                thumbnailPromises.push(zipEntry.async('blob').then((blob) => {
                                    return {
                                        name: relativePath, // Get the file name
                                        url: URL.createObjectURL(blob) // Create a URL for the image blob,
                                    };
                                }));
                            }
                        });

                        const thumbnailResponses = await Promise.all(thumbnailPromises);
                        if (document && document.thumbnails) {
                            document.thumbnails.forEach((thumbnail) => {
                                const image = thumbnailResponses.find(x => x.name.includes(thumbnail.id));
                                thumbnail.src = image?.url;
                                thumbnail.sasUrl = document.sasUrl;
                                thumbnail.attachmentType = this.isPDF(thumbnail.sasUrl) ? AttachmentType.Object : AttachmentType.Image;
                            });
                        }
                    } else {
                        document.thumbnails.forEach((thumbnail => {
                            thumbnail.sasUrl = document.sasUrl,
                            thumbnail.attachmentType = this.isPDF(document.sasUrl) ? AttachmentType.Object : AttachmentType.Image,
                            thumbnail.src = this.isImage(document.sasUrl) ? document.sasUrl : null
                        }));
                    }
                }));
                return documentLinks.flatMap(x => x.thumbnails);
            }

            this.toggleInspectionDetailDrawer({
                getAttachmentDocuments: getAttachmentDocuments,
                title: AttachmentDrawerSection.CloseOutDocuments,
                selectedThumbnailId: thumbnailId,
                count: rsDocumentLinksResponse.documentLinks.flatMap(x => x.thumbnails).length || 0
            });
        }catch(e){
            this.loadingCloseOutDocuments = false;
            console.error(e);
        }
    }

    setFilteredNonConformities()
    {
        type Predicate<T> = (item: T) => boolean;
        const predicatesRiskLevel: Predicate<any>[] = [];
        const predicatesRiskClassification: Predicate<any>[] = [];

        if(this.riskLevelHighActive) {
            predicatesRiskLevel.push((x) => (x.riskLevelId ? x.riskLevelId : x.defaultRiskLevelId) == FindingRiskLevelEnum.High);
        }
        
        if(this.riskLevelMediumActive) {
            predicatesRiskLevel.push((x) => (x.riskLevelId ? x.riskLevelId : x.defaultRiskLevelId) == FindingRiskLevelEnum.Medium);
        }
        
        if (this.riskLevelLowActive) {
            predicatesRiskLevel.push((x) => (x.riskLevelId ? x.riskLevelId : x.defaultRiskLevelId) == FindingRiskLevelEnum.Low);
        }
        
        if (this.regulatoryFilterActive) {
            predicatesRiskClassification.push((x) => x.riskClassificationId === 1);
        }
        
        if (this.industryStandardFilterActive) {
            predicatesRiskClassification.push((x) => x.riskClassificationId !== 1);
        }

        this.filteredNonConformities = this.nonConformitiesPerSection.filter((x) =>
            (predicatesRiskLevel.length == 0 || predicatesRiskLevel.some(p => p(x))) &&
            (predicatesRiskClassification.length == 0 || predicatesRiskClassification.some(p => p(x)))
        );
        
        return this.filteredNonConformities;
    }

    regulatoryFilterClick(){
        this.regulatoryFilterActive = !this.regulatoryFilterActive;
        this.setFilteredNonConformities();
    }

    industryStandardFilterClick(){
        this.industryStandardFilterActive = !this.industryStandardFilterActive;
        this.setFilteredNonConformities();
    }

    riskLevelHighClick(){
        this.riskLevelHighActive = !this.riskLevelHighActive;
        this.setFilteredNonConformities();
    }

    riskLevelMediumClick(){
        this.riskLevelMediumActive = !this.riskLevelMediumActive;
        this.setFilteredNonConformities();
    }

    riskLevelLowClick(){
        this.riskLevelLowActive = !this.riskLevelLowActive;
        this.setFilteredNonConformities();
    }

    clearAllFilters(){
        this.riskLevelHighActive = false;
        this.riskLevelMediumActive = false;
        this.riskLevelLowActive = false;
        this.regulatoryFilterActive = false;
        this.industryStandardFilterActive = false;

        return this.setFilteredNonConformities();
    }

    checkSectionAvailability(section){
        if (this.sectionCounts[section].isHidden)
            return "N/A"
        return this.sectionCounts[section].count;
    }

    isSectionHidden(section) {
        return section.sectionNumber && Object.keys(this.sectionCounts).indexOf(section.sectionNumber) > -1?
             this.sectionCounts[section.sectionNumber].isHidden: true;
    }

    activate(params: { item: RightShipInspectionClientModel, toggleInspectionDetailDrawer: (model: AttachmentViewerExecutionModel) => void }) {
        this.item = params.item;
        this.toggleInspectionDetailDrawer = params.toggleInspectionDetailDrawer;
        this.questionNumber = this.item.questionNumber;
        if(this.questionNumber) this.scrollToFinding(this.questionNumber);
    }

    async attached()
    {
        this.totalAttachmentCount = await this.rightshipInspectionService.getFindingAllDocumentsCount(this.item.rightShipInspectionId);
        await this.getNonConformities()
        setTimeout(() => {
            this.selectAllSections();
            if(this.questionNumber) this.scrollToFinding(this.questionNumber);
        }, 100);
    }

    async getNonConformities()
    {
        this.isLoading = true;
        const resp = await this.rightshipInspectionService.getRSInspectionNonConformity(this.item.rightShipInspectionId)
        this.nonConformityRFI = resp.nonConformityRFI;
        this.sectionCounts = resp.sectionNonConformityCounts;
        this.nonConformities = resp.nonConformities;
        this.item.hasIncompleteFinding = this.nonConformities.some(c => !c.isGradingConfirmed);
        this.totalFindingsCount = this.nonConformities.length
        this.nonConformityCloseoutSubtaskType = resp.nonConformityCloseoutSubtaskType;
        this.nonConformitiesSections = Enumerable.from(resp.nonConformities)
            .select(nc => { return { sectionId: nc.sectionId, sectionNumber: nc.sectionNumber, sectionName: nc.sectionName, sectionSequence: nc.sectionSequence}; })
            .distinct(resp => resp.sectionId).orderBy(resp => resp.sectionSequence).toArray();
        this.selectAllSections();
        await this.setNonConformitiesExtractionThumbnails();
        this.isLoading = false;
    }

    async setNonConformitiesExtractionThumbnails() {
        this.nonConformities.forEach(async (nc) => {
            if (nc.aiRunId) {
                const zip = new JSZip();
                const thumbnailResponse = await this.rightshipInspectionService.getInspectionThumbnailZipFile(nc.aiRunId);
                await zip.loadAsync(thumbnailResponse);

                const thumbnailPromises = [];
                zip.forEach((relativePath, zipEntry) => {
                    if (!zipEntry.dir && /\.(jpg|jpeg|png|gif)$/.test(relativePath)) { // Filter for image types
                        thumbnailPromises.push(zipEntry.async('blob').then((blob) => {
                            return {
                                name: relativePath, // Get the file name
                                url: URL.createObjectURL(blob) // Create a URL for the image blob,
                            };
                        }));
                    }
                });

                const thumbnailResponses = await Promise.all(thumbnailPromises);
                if (nc.thumbnails) {
                    // Every thumbnail in 1 nonConformities count as 1 attachment
                    nc.thumbnails.forEach((thumbnail) => {
                        const image = thumbnailResponses.find(x => x.name.includes(thumbnail.id));
                        thumbnail.src = image?.url;
                        this._thumbnails.push({ name: thumbnail.fileName, url: image?.url });
                    });
                }
            }
        });
    }

    reload = () => {
        this.getNonConformities().then(() => {
            if(this.findingsSectionInView)
                this.selectFindingSection(this.findingsSectionInView);
            else this.selectAllSections();

            //Query through the findings and select the one where the data-id is equal to the selected finding id
            const activeElement = document.querySelectorAll(`[data-id='${this.selectedFindingId}']`)[0];

            //Scroll finding back into view
            if(activeElement) {
                activeElement.scrollIntoView({ block: 'start', behavior: "smooth"})
            }
        });
    }

    //Scroll to the finding secified
    scrollToFinding(questionNumber: string) {
        if(this.filteredNonConformities && this.filteredNonConformities.length > 0) {
            const index = this.filteredNonConformities.findIndex(x => x.questionNumber === questionNumber);
            const nonConformity = this.filteredNonConformities[index];
            if (nonConformity && nonConformity.element) {
                nonConformity.element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                if(this.isCollapsed(index)) {
                    this.toggleSectionsCollapsed(index);
                    this.bindingSignaler.signal('update-all-sections-collapsed');
                }
            }
        }
    }

    async openFile(file) {
        if (file && file.path) {
            if(!file.fullUrl) {
                await this.addAttachmentToken(file);
            }
            const path = file.fullUrl;
            const downloadFile = !(this.isPDF(path) || this.isImage(path));


            if (!downloadFile) {
                this.dialogService.show(CommonUiDialogConstants.ObjectViewer,
                    {
                        link: path,
                        doNotAddExtension: true
                    }, false, false, true);
            }
            else {
                FileHelper.downloadFile(AttachmentsHelper.getUrl(path, this.blob, null), file.documentName);
            }
        }
    }

    async getFileUrl(file) {
        if (file && file.path && this.isImage(file.path)) {
            if (!file.fullUrl) await this.addAttachmentToken(file);
            if(file.fullUrl){
                return file.fullUrl;
            }
        }
        return require('../../../images/icons/icon-document.svg');
    }

    addAttachmentToken(file)
    {
        return new Promise<string>((resolve, reject) => {
            if (file && file.path) {
                let path = AttachmentsHelper.getUrl(file.path, this.blob, null);
                const downloadFile = !(this.isPDF(path) || this.isImage(path));
                this.attachmentService.getSharedAccessSignatureToken(file.entityAttachmentId, downloadFile).then(sasToken => {
                    if (sasToken) {
                        path += ((sasToken.startsWith("?") ? "" : "?") + sasToken);
                    }

                    file.fullUrl = AttachmentsHelper.getUrl(path, this.blob, null);

                    resolve(file.fullUrl);
                }).catch(e => reject(e));
            }
        });
    }

    private isPDF(link): boolean {
        return link.indexOf('.pdf') != -1;
    }

    private isImage(link): boolean {
        const imageExtensions = ["jpg", "jpeg", "png", "gif", "svg", "heic"];
        const lowerCaseLink = link.toLowerCase();
        return imageExtensions.some(imageExtension => lowerCaseLink.indexOf(imageExtension) != -1);
    }

    getRiskLevelClass(conformity:RightShipInspectionNonConformityModel) {
        const riskLevel = conformity.riskLevelId ? conformity.riskLevelId : conformity.defaultRiskLevelId;
        switch(riskLevel) {
            case 1:
                return "High";
            case 2:
                return "Medium";
            case 3:
            default:
                return "Low";
        }
    }

    isSwitchEnabled: boolean = false;
    isAllSectionsCollapsed: boolean = false;
    sectionCollapsableToggled: {[key: number]: boolean} = {};

    resetCollapsableToggles()
    {
        this.isSwitchEnabled = true;
        this.isAllSectionsCollapsed = true;
        this.sectionCollapsableToggled = {};
        if (this.filteredNonConformities && this.filteredNonConformities.length > 0) {
            this.bindingSignaler.signal('update-all-sections-collapsed');
        }
    }

    toggleAllSectionsCollapsed() {
        this.isAllSectionsCollapsed = !this.isSwitchEnabled;

        //Clear array
        this.sectionCollapsableToggled = {};
        if (this.filteredNonConformities && this.filteredNonConformities.length > 0) {
            this.bindingSignaler.signal('update-all-sections-collapsed');
        }
    }

    toggleSectionsCollapsed(index: number) {
        if(!this.sectionCollapsableToggled) this.sectionCollapsableToggled = {};
        if(!this.isAllSectionsCollapsed && Object.keys(this.sectionCollapsableToggled).indexOf(index.toString()) == -1)
            this.sectionCollapsableToggled[index] = true;

        this.sectionCollapsableToggled[index] = 
            //true when is open. set to true if finding index is false or is not in sectionCollapsableToggled
            !(this.sectionCollapsableToggled && Object.keys(this.sectionCollapsableToggled).indexOf(index.toString()) > -1 && this.sectionCollapsableToggled[index]);

        this.isSwitchEnabled = Object.keys(this.sectionCollapsableToggled).filter(k => this.sectionCollapsableToggled[k] == true).length == 0;
    }

    isCollapsed(index: number)
    {
        const isKeyInTogglesObj = this.sectionCollapsableToggled && Object.keys(this.sectionCollapsableToggled).indexOf(index.toString()) > -1;
        const isSectionToggled =  isKeyInTogglesObj && (this.sectionCollapsableToggled[index] ?? false);
        const collapsableOpen =  (!this.isAllSectionsCollapsed && (!isKeyInTogglesObj || isSectionToggled)) || (this.isAllSectionsCollapsed && isSectionToggled);
        return !collapsableOpen;
    }

    navigateToAllAttachmentThumbnail(id: string) {
        this.selectAllAttachments(id);
    }
}
